import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mic, Square } from 'lucide-react';
import { klarysUrl } from './klarysApi';

const detecterSilence = (stream, onSilence) => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  const ctx = new Ctx();
  const an = ctx.createAnalyser();
  ctx.createMediaStreamSource(stream).connect(an);
  const buf = new Uint8Array(an.fftSize);
  let parle = false;
  let calme = Date.now();
  const debut = Date.now();
  const boucle = () => {
    if (stream.getTracks().every((t) => t.readyState === 'ended')) { ctx.close(); return; }
    an.getByteTimeDomainData(buf);
    const niveau = Math.max(...buf.map((v) => Math.abs(v - 128)));
    if (niveau > 12) { parle = true; calme = Date.now(); }
    if ((parle && Date.now() - calme > 1600) || Date.now() - debut > 30000) { ctx.close(); onSilence(); return; }
    requestAnimationFrame(boucle);
  };
  boucle();
};

export const KlarysMic = ({ headers, onTexte, disabled, declencheur }) => {
  const [etat, setEtat] = useState('repos');
  const rec = useRef(null);
  useEffect(() => { if (declencheur) demarrer(); }, [declencheur]); // eslint-disable-line react-hooks/exhaustive-deps

  const demarrer = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Dictée indisponible', { description: "Votre navigateur ne permet pas l'enregistrement audio." });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      const morceaux = [];
      mr.ondataavailable = (e) => e.data.size && morceaux.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setEtat('transcription');
        try {
          const fd = new FormData();
          fd.append('fichier', new Blob(morceaux, { type: mime }), mime === 'audio/webm' ? 'dictee.webm' : 'dictee.m4a');
          const r = await axios.post(klarysUrl('/transcrire'), fd, { headers });
          if (r.data.texte) onTexte(r.data.texte);
          else toast.info('Aucune parole détectée');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Transcription impossible');
        } finally {
          setEtat('repos');
        }
      };
      rec.current = mr;
      mr.start();
      setEtat('ecoute');
      if (declencheur) detecterSilence(stream, () => mr.state !== 'inactive' && mr.stop());
    } catch (err) {
      toast.error('Micro inaccessible', { description: "Autorisez l'accès au micro dans votre navigateur." });
    }
  };

  const arreter = () => rec.current && rec.current.state !== 'inactive' && rec.current.stop();

  if (etat === 'transcription') {
    return <span className="flex h-11 w-11 items-center justify-center rounded-md border border-slate-200" data-testid="klarys-mic-transcription"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></span>;
  }
  return etat === 'ecoute' ? (
    <button type="button" onClick={arreter} data-testid="klarys-mic-stop" aria-label="Arrêter la dictée"
      className="flex h-11 w-11 items-center justify-center rounded-md bg-rose-600 text-white animate-pulse">
      <Square className="h-4 w-4" />
    </button>
  ) : (
    <button type="button" onClick={demarrer} disabled={disabled} data-testid="klarys-mic-btn" aria-label="Dicter"
      className="flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:border-[#0a3d62] hover:text-[#0a3d62] disabled:opacity-40">
      <Mic className="h-4 w-4" />
    </button>
  );
};
